/**
 * Image faded by a linear alpha mask (opacity gradient).
 * Uses `react-native-svg` Mask — same rendering on iOS and Android. No Skia / MaskedView.
 *
 * White (opaque) in the mask = image fully visible; transparent = image hidden.
 * Optional `tintColor` washes the opaque edge and fades out with the same gradient.
 *
 * The bitmap is object-fit: cover into the cover box (`imageWidth` × `imageHeight`),
 * then clipped to the visible mask (`width` × `height`).
 */
import React, { memo, useId, useMemo } from 'react';
import {
  StyleSheet,
  View,
  type ImageSourcePropType,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import Svg, {
  ClipPath,
  Defs,
  FeGaussianBlur,
  Filter,
  Image as SvgImage,
  LinearGradient,
  Mask,
  Rect,
  Stop,
} from 'react-native-svg';

import { parseSvgGradientStop } from './gradientSvgColor';

export type GradientMaskedImageProps = {
  source: ImageSourcePropType;
  /** Visible / mask width. */
  width: number;
  /** Visible / mask height (clips the image). */
  height: number;
  /**
   * Cover-box height for object-fit cover (e.g. full window height when masking only a top band).
   * Defaults to `height`.
   */
  imageHeight?: number;
  /**
   * Cover-box width for object-fit cover. Defaults to `width`.
   */
  imageWidth?: number;
  /**
   * Which edge is fully visible (opacity 1).
   * `top` = opaque at top → transparent at bottom (status / soft frost).
   */
  opaqueEdge?: 'top' | 'bottom';
  /** Optional SVG gaussian blur (approximate; platforms differ slightly). */
  blurStdDeviation?: number;
  /**
   * Solid wash on the opaque edge (e.g. `#ffffff`, `rgba(...)`, theme primary).
   * Fades to transparent toward the other edge with the same mask direction.
   */
  tintColor?: string;
  /** Peak opacity of `tintColor` at the opaque edge (0–1). Default 0.45. */
  tintOpacity?: number;
  style?: StyleProp<ViewStyle>;
  pointerEvents?: 'none' | 'box-none' | 'box-only' | 'auto';
};

export const GradientMaskedImage = memo(function GradientMaskedImage({
  source,
  width,
  height,
  imageHeight,
  imageWidth,
  opaqueEdge = 'top',
  blurStdDeviation,
  tintColor,
  tintOpacity = 0.45,
  style,
  pointerEvents = 'none',
}: GradientMaskedImageProps) {
  const react_id = useId().replace(/:/g, '');
  const mask_id = `gmi-mask-${react_id}`;
  const clip_id = `gmi-clip-${react_id}`;
  const grad_id = `gmi-grad-${react_id}`;
  const tint_grad_id = `gmi-tint-${react_id}`;
  const filter_id = `gmi-blur-${react_id}`;

  // Cover box (screen-sized for frost) — not the cover *result* size.
  // `xMidYMid slice` maxes the nearer axis to this box (object-fit: cover).
  const cover_w = imageWidth ?? width;
  const cover_h = imageHeight ?? height;
  const top_opaque = opaqueEdge === 'top';

  const stops = useMemo(
    () =>
      top_opaque
        ? [
            { offset: '0', opacity: 1 },
            { offset: '1', opacity: 0 },
          ]
        : [
            { offset: '0', opacity: 0 },
            { offset: '1', opacity: 1 },
          ],
    [top_opaque],
  );

  const tint_stops = useMemo(() => {
    if (tintColor == null || tintColor === '') {
      return null;
    }
    const parsed = parseSvgGradientStop(tintColor);
    const peak = Math.min(1, Math.max(0, tintOpacity)) * parsed.stopOpacity;
    const solid = { color: parsed.stopColor, opacity: peak };
    const clear = { color: parsed.stopColor, opacity: 0 };
    return top_opaque
      ? [
          { offset: '0', ...solid },
          { offset: '1', ...clear },
        ]
      : [
          { offset: '0', ...clear },
          { offset: '1', ...solid },
        ];
  }, [tintColor, tintOpacity, top_opaque]);

  if (width <= 0 || height <= 0) {
    return null;
  }

  const use_blur = blurStdDeviation != null && blurStdDeviation > 0;

  return (
    <View style={[styles.root, { width, height }, style]} pointerEvents={pointerEvents}>
      <Svg width={width} height={height}>
        <Defs>
          <LinearGradient id={grad_id} x1="0" y1="0" x2="0" y2="1">
            {stops.map(stop => (
              <Stop
                key={stop.offset}
                offset={stop.offset}
                stopColor="#ffffff"
                stopOpacity={String(stop.opacity)}
              />
            ))}
          </LinearGradient>
          {tint_stops ? (
            <LinearGradient id={tint_grad_id} x1="0" y1="0" x2="0" y2="1">
              {tint_stops.map(stop => (
                <Stop
                  key={`tint-${stop.offset}`}
                  offset={stop.offset}
                  stopColor={stop.color}
                  stopOpacity={String(stop.opacity)}
                />
              ))}
            </LinearGradient>
          ) : null}
          <ClipPath id={clip_id}>
            <Rect x={0} y={0} width={width} height={height} />
          </ClipPath>
          <Mask
            id={mask_id}
            x={0}
            y={0}
            width={width}
            height={height}
            maskUnits="userSpaceOnUse"
            maskContentUnits="userSpaceOnUse">
            <Rect x={0} y={0} width={width} height={height} fill={`url(#${grad_id})`} />
          </Mask>
          {use_blur ? (
            <Filter id={filter_id} x="-5%" y="-5%" width="110%" height="110%">
              <FeGaussianBlur stdDeviation={blurStdDeviation} />
            </Filter>
          ) : null}
        </Defs>
        <SvgImage
          href={source}
          x={0}
          y={0}
          width={cover_w}
          height={cover_h}
          preserveAspectRatio="xMidYMid slice"
          clipPath={`url(#${clip_id})`}
          mask={`url(#${mask_id})`}
          {...(use_blur ? { filter: `url(#${filter_id})` } : {})}
        />
        {tint_stops ? (
          <Rect x={0} y={0} width={width} height={height} fill={`url(#${tint_grad_id})`} />
        ) : null}
      </Svg>
    </View>
  );
});

const styles = StyleSheet.create({
  root: {
    overflow: 'hidden',
  },
});
